import { defineStore } from 'pinia';
import crmApi from '../api/crmApi';

const hasAnyFilter = filters => {
  return Object.values(filters || {}).some(value => {
    return value !== undefined && value !== null && value !== '';
  });
};

const mapOrdersByColumnId = orders => {
  return (orders || []).reduce((acc, order) => {
    const columnId = order.kanban_column_id;
    if (!columnId) {
      return acc;
    }

    if (!acc[columnId]) {
      acc[columnId] = [];
    }

    acc[columnId].push(order);
    return acc;
  }, {});
};

const sortOrders = orders => {
  return [...orders].sort((left, right) => {
    return (left.position || 0) - (right.position || 0);
  });
};

export const useCrmStore = defineStore('crm', {
  state: () => ({
    columns: [],
    uiFlags: {
      isFetching: false,
      isMutating: false,
    },
  }),
  getters: {
    getColumns: state => state.columns,
    getIsFetching: state => state.uiFlags.isFetching,
    getIsMutating: state => state.uiFlags.isMutating,
  },
  actions: {
    replaceColumns(columns = []) {
      this.columns = columns;
    },

    async fetchKanbanData({ accountId, pipeline, filters = {} }) {
      this.uiFlags.isFetching = true;
      try {
        if (!hasAnyFilter(filters)) {
          const response = await crmApi.getColumns(accountId, pipeline);
          this.columns = response.data.payload || [];
          return;
        }

        const [columnsResponse, serviceOrdersResponse] = await Promise.all([
          crmApi.getColumns(accountId, pipeline),
          crmApi.getServiceOrders(accountId, { pipeline, filters }),
        ]);

        const columns = columnsResponse.data.payload || [];
        const serviceOrders = serviceOrdersResponse.data.payload || [];
        const ordersByColumn = mapOrdersByColumnId(serviceOrders);

        this.columns = columns.map(column => ({
          ...column,
          service_orders: sortOrders(ordersByColumn[column.id] || []),
        }));
      } finally {
        this.uiFlags.isFetching = false;
      }
    },

    async moveOrder({
      accountId,
      orderId,
      kanbanColumnId,
      position,
      pipeline,
    }) {
      await crmApi.updateServiceOrder(
        accountId,
        orderId,
        {
          kanban_column_id: kanbanColumnId,
          position,
        },
        pipeline
      );
    },

    async createServiceOrder({ accountId, orderData, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await crmApi.createServiceOrder(accountId, orderData, pipeline);
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },

    async updateServiceOrder({ accountId, orderId, orderData, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await crmApi.updateServiceOrder(
          accountId,
          orderId,
          orderData,
          pipeline
        );
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },

    async deleteServiceOrder({ accountId, orderId, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await crmApi.deleteServiceOrder(accountId, orderId, pipeline);
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },

    async createKanbanColumn({ accountId, columnData, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await crmApi.createColumn(accountId, columnData, pipeline);
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },

    async updateKanbanColumn({ accountId, columnId, columnData, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await crmApi.updateColumn(accountId, columnId, columnData, pipeline);
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },

    async deleteKanbanColumn({ accountId, columnId, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await crmApi.deleteColumn(accountId, columnId, pipeline);
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },

    async reorderKanbanColumns({ accountId, columnIdsInOrder, pipeline }) {
      this.uiFlags.isMutating = true;
      try {
        await Promise.all(
          columnIdsInOrder.map((columnId, index) =>
            crmApi.updateColumn(
              accountId,
              columnId,
              { position: index + 1 },
              pipeline
            )
          )
        );
        await this.fetchKanbanData({ accountId, pipeline });
      } finally {
        this.uiFlags.isMutating = false;
      }
    },
  },
});
